/**
 * Envío de órdenes de venta a SAP B1 (`POST integration/sapb1/orders`).
 *
 * Lo usan **`SalesOrderList.js`** y **`BuyOrderList.js`** (botón «Procesar a SAP»).
 *
 * El envío es **asíncrono**: el backend responde 202 con un `jobId` y procesa las OV **una por una** en
 * segundo plano; acá se consulta el avance hasta que termina. Antes era síncrono y un lote grande mantenía
 * el request abierto por minutos, hasta que Cloudflare lo cortaba con **524** a los ~100 s — mientras el
 * backend seguía creando las OV en SAP y el usuario solo veía «Network Error».
 *
 * Mismo patrón que `salesOrdersWholesaleImport.js` (job + polling) para la carga de Excel.
 */
import axiosInstance from '../utils/axiosInstance';

/** Desenvuelve el `APIResponse` del backend (`{ code, message, data }`). */
function unwrap(responseData) {
  if (responseData && typeof responseData === 'object' && responseData.data != null) {
    return responseData.data;
  }
  return responseData;
}

/**
 * Encola el envío a SAP. Responde apenas el backend acepta el trabajo, sin esperar a SAP.
 *
 * Tolera las dos formas de respuesta del backend:
 * - **asíncrona** (la normal): 202 con `jobId`, y el resultado se sigue con `pollSapSendJob`;
 * - **síncrona**: 200 con `results` / `successCount` ya listos, que es lo que devuelve un backend sin
 *   este cambio, o con el interruptor `sap.orders.async-enabled=false`. Sin este soporte el diálogo de
 *   resultados mostraría 0 exitosas aunque las OV se hayan creado en SAP.
 *
 * @param {string[]} orderIds
 * @param {Array<{orderId: string, itemId: string, quantityToSend: number, brokeQuantity: number}>} [itemAdjustments]
 * @returns {Promise<{jobId: string|null, status: string, total: number, rejected: Array<object>, syncResult: object|null}>}
 */
export async function sendSalesOrdersToSap(orderIds, itemAdjustments) {
  const body = { orderIds };
  if (itemAdjustments) {
    body.itemAdjustments = itemAdjustments;
  }
  const response = await axiosInstance.post('integration/sapb1/orders', body);
  const job = unwrap(response.data) || {};
  const isSyncResponse = !job.jobId && Array.isArray(job.results);
  return {
    jobId: job.jobId || null,
    status: job.status,
    total: job.total ?? (isSyncResponse ? job.results.length : 0),
    rejected: job.rejected || [],
    syncResult: isSyncResponse ? job : null
  };
}

/**
 * Consulta el avance hasta que el envío termina.
 *
 * El intervalo **crece**: arranca corto, porque al principio es cuando el usuario mira el contador,
 * y se va espaciando hasta el techo. Con intervalo fijo de 3 s, un envío de 2 minutos dejaba ~40
 * consultas en la pestaña de red y costaba encontrar el request que realmente falló; con backoff
 * son unas 10, y el avance se sigue viendo igual de rápido al comienzo.
 *
 * @param {string} jobId
 * @param {{ initialIntervalMs?: number, maxIntervalMs?: number, backoffFactor?: number,
 *           maxWaitMs?: number, maxAttempts?: number,
 *           onProgress?: (p: {processed: number, total: number}) => void }} [options]
 * @returns {Promise<object>} Job final: `{ status, total, processed, successCount, failureCount, results, rejected }`.
 *                            Rechaza si el trabajo falla, si el job ya no existe o si se agota la espera.
 */
export async function pollSapSendJob(jobId, options = {}) {
  const {
    initialIntervalMs = 2000,
    maxIntervalMs = 15000,
    backoffFactor = 1.5,
    maxWaitMs = 15 * 60 * 1000,
    maxAttempts = 200,
    onProgress = null
  } = options;

  let waitMs = Math.max(0, initialIntervalMs);
  let elapsedMs = 0;

  for (let attempt = 0; attempt < maxAttempts && elapsedMs < maxWaitMs; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    elapsedMs += waitMs;
    waitMs = Math.min(maxIntervalMs, waitMs * backoffFactor);

    let response;
    try {
      response = await axiosInstance.get(`integration/sapb1/orders/jobs/${jobId}`);
    } catch (err) {
      if (err?.response?.status === 404) {
        // El job vive en memoria del backend: si se reinició o expiró, no podemos seguir el avance.
        // No significa que el envío falló, así que el mensaje apunta a verificar la lista.
        throw new Error(
          'No se pudo seguir el avance del envío. Recargue la lista de OV para ver el resultado.'
        );
      }
      // Errores de red transitorios (502, etc.): reintentar silenciosamente
      console.warn(`[pollSapSendJob] intento=${attempt + 1} error de red:`, err?.message);
      continue;
    }

    const job = unwrap(response.data) || {};
    if (onProgress) {
      onProgress({ processed: job.processed || 0, total: job.total || 0 });
    }

    if (job.status === 'SUCCESS') return job;
    if (job.status === 'FAILED') {
      throw new Error(job.errorMessage || 'El envío a SAP falló en el servidor.');
    }
    // PENDING o PROCESSING → seguir esperando
  }

  throw new Error(
    `El envío a SAP tardó más de lo esperado (${Math.max(1, Math.round(elapsedMs / 60000))} min). ` +
      'Recargue la lista de OV para verificar cuáles se enviaron.'
  );
}

/**
 * Envía y espera el resultado, juntando las OV rechazadas en la revisión previa con las procesadas,
 * en el mismo shape que consumía la respuesta síncrona anterior
 * (`successCount` / `failureCount` / `results`), para no tener que cambiar el diálogo de resultados.
 *
 * @param {string[]} orderIds
 * @param {Array<object>} [itemAdjustments]
 * @param {{ onProgress?: (p: {processed: number, total: number}) => void }} [options]
 */
export async function sendSalesOrdersToSapAndWait(orderIds, itemAdjustments, options = {}) {
  const { jobId, rejected, syncResult } = await sendSalesOrdersToSap(orderIds, itemAdjustments);
  // Backend síncrono: el resultado ya vino completo, no hay avance que seguir.
  if (syncResult) {
    return mergeJobWithRejected(syncResult, rejected);
  }
  const job = jobId ? await pollSapSendJob(jobId, options) : { results: [] };
  return mergeJobWithRejected(job, rejected);
}

/** Junta resultados y rechazos en un solo objeto con los contadores ya calculados. */
export function mergeJobWithRejected(job, rejectedFromPost) {
  const processed = job?.results || [];
  // El job también trae los rechazos; se prefiere esa copia y el POST queda como respaldo.
  const rejected = (job?.rejected?.length ? job.rejected : rejectedFromPost) || [];
  const results = [...processed, ...rejected];
  const successCount = results.filter((r) => r.success).length;
  return {
    results,
    successCount,
    failureCount: results.length - successCount
  };
}
